import type { CSSProperties } from 'react';
import {
  Sparkles, Boxes, MessageSquare, Trash2, Package, Bot, BookOpen, Trophy, Bug, Library,
  Eye, EyeOff, Rocket, Zap, Link2, Compass, Lock, Unlock, AlertTriangle, Hourglass,
  Copy, BarChart3, Wrench, Mail, Dna, Briefcase, Pin, Image as ImageIcon, Coins, Users,
  HelpCircle, Inbox, Eraser, GitMerge, FlaskConical, Hammer, Puzzle, GraduationCap,
  Paperclip, Handshake, PartyPopper, BadgePlus, Satellite, Brain, StickyNote, Scissors,
  Recycle, GripVertical, Info, Share2, BookOpenCheck, ClipboardList, Server, Shapes,
  Network, Activity, Braces, MessagesSquare, UserX,
  Type, Bold, Italic, Underline, Strikethrough, List, ListOrdered, Baseline,
  Highlighter, PaintBucket, ALargeSmall, SquareDashed,
  type LucideIcon,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, GitCommitHorizontal,
  Plus, PanelTop, History as HistoryGlyph, Palette,
} from 'lucide-react';
import { ICON_DATA, type IconFamily } from './iconData';
import { useThemeStore, type ThemePalette } from './stores';
import type { FilterIconName } from '@pcp/shared';
import { FILTER_ICON_COMPONENTS } from './filterIconMap';

/**
 * Central, semantic icon registry. Use <Icon name="…"/> everywhere instead of
 * hard-coding emoji. The active theme picks the icon FAMILY (per-theme DNA);
 * Lucide is the fallback if a family lacks a glyph. Bodies use currentColor, so
 * icons follow the theme (light/dark + accent). Names not in the per-family SVG
 * data fall through to a Lucide component (see LUCIDE_COMPONENTS).
 */
export type IconName =
  // nav
  | 'dashboard'
  | 'topics'
  | 'tickets'
  | 'tenants'
  | 'backlog'
  | 'companies'
  | 'commits'
  | 'releases'
  | 'entitlements'
  | 'insights'
  | 'documentation'
  | 'settings'
  | 'home'
  | 'more'
  // ui / filters / meta
  | 'search'
  | 'filter'
  | 'refresh'
  | 'send'
  | 'support'
  | 'sort'
  | 'status'
  | 'reviewed'
  | 'tag'
  | 'department'
  | 'type'
  | 'check'
  | 'x'
  | 'chevronRight'
  | 'chevronDown'
  | 'conversation'
  | 'analysis'
  | 'user'
  /** Nobody — an item with no assignee, as the Backlog's People filter offers it.
   *  A crossed-out person, so it reads as the absence of one beside `user`. */
  | 'unassigned'
  | 'folder'
  | 'calendar'
  | 'clock'
  | 'edit'
  | 'priority'
  | 'design'
  // actions / objects (Lucide-backed; see LUCIDE_COMPONENTS)
  | 'ai'
  | 'workitem'
  | 'comment'
  | 'delete'
  | 'package'
  | 'analyze'
  | 'userstory'
  | 'feature'
  | 'bug'
  /** Feedback boards — the Backlog area's canvas entity. Stacked bubbles, so it
   *  reads as "a conversation about this" and stays distinct from `comment`
   *  (a single bubble) at 15px. */
  | 'feedback'
  | 'sources'
  | 'view'
  | 'hide'
  | 'rocket'
  | 'zap'
  | 'link'
  | 'compass'
  | 'lock'
  | 'unlock'
  | 'warning'
  | 'pending'
  | 'copy'
  | 'chart'
  | 'tools'
  | 'mail'
  | 'variant'
  | 'briefcase'
  | 'pin'
  | 'image'
  | 'credits'
  | 'users'
  | 'help'
  | 'inbox'
  | 'clean'
  | 'merge'
  | 'test'
  | 'build'
  | 'puzzle'
  | 'learning'
  | 'attach'
  | 'handshake'
  | 'celebrate'
  | 'new'
  | 'satellite'
  | 'thinking'
  | 'note'
  /** A rich-text panel on the feedback canvas — the element, and its toolbar
   *  button. A letterform, so it reads as "text" beside `note`'s sticky and
   *  `image`'s picture. (f-01954) */
  | 'textBlock'
  // The text block's formatting toolbar. Standard editor glyphs, and the one place
  // in the app that needs them.
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'bulletList'
  | 'numberList'
  /** Type size — a letter with a baseline, not a magnifier. */
  | 'textSize'
  /** The colour of the TEXT (an underscored A). */
  | 'textColor'
  /** The colour BEHIND the text, one run at a time. */
  | 'highlight'
  /** The block's own paper. */
  | 'fillColor'
  /** The block's own frame. */
  | 'borderColor'
  | 'cut'
  | 'recycle'
  | 'info'
  | 'grip'
  // Reorder: the single-pointer alternative to dragging (WCAG 2.2 SC 2.5.7).
  | 'arrowUp'
  | 'arrowDown'
  /** A connector on the feedback canvas: a line between two endpoints. (f-01961) */
  | 'connector'
  /** The two arrowhead toggles on a connector's toolbar. */
  | 'arrowLeft'
  | 'arrowRight'
  | 'share'
  | 'guides'
  | 'playbook'
  | 'mcp'
  // The public Status Page admin area (an uptime pulse). NOT 'status', which is
  // already the generic list-filter glyph in every per-family icon set.
  | 'statusPage'
  /** A merge field / template placeholder. Braces, because a merge field
   *  literally IS `{{ … }}` — a tag icon reads as a LABEL, which is a
   *  different idea entirely. */
  | 'mergeField'
  // Filter framework chrome (f-02064): the + of "+ Filter", the tab-row
  // placement marker, a filter's version history, and its colour.
  | 'add'
  | 'tab'
  | 'history'
  | 'palette'
  | 'general';

/** Lucide components for names not carried by the per-family SVG registries. */
const LUCIDE_COMPONENTS: Partial<Record<IconName, LucideIcon>> = {
  ai: Sparkles, workitem: Boxes, comment: MessageSquare, delete: Trash2, package: Package,
  analyze: Bot, userstory: BookOpen, feature: Trophy, bug: Bug, sources: Library,
  feedback: MessagesSquare,
  view: Eye, hide: EyeOff, rocket: Rocket, zap: Zap, link: Link2, compass: Compass,
  lock: Lock, unlock: Unlock, warning: AlertTriangle, pending: Hourglass, copy: Copy,
  chart: BarChart3, tools: Wrench, mail: Mail, variant: Dna, briefcase: Briefcase,
  pin: Pin, image: ImageIcon, credits: Coins, users: Users, help: HelpCircle, inbox: Inbox,
  unassigned: UserX,
  clean: Eraser, merge: GitMerge, test: FlaskConical, build: Hammer, puzzle: Puzzle,
  learning: GraduationCap, attach: Paperclip, handshake: Handshake, celebrate: PartyPopper,
  new: BadgePlus, satellite: Satellite, thinking: Brain, note: StickyNote, cut: Scissors,
  recycle: Recycle, grip: GripVertical, info: Info, share: Share2, guides: BookOpenCheck,
  textBlock: Type, bold: Bold, italic: Italic, underline: Underline,
  strikethrough: Strikethrough, bulletList: List, numberList: ListOrdered,
  // A big A beside a small one — the size control, distinct from `textBlock`'s
  // plain letterform, which is the element itself.
  textSize: ALargeSmall, textColor: Baseline, highlight: Highlighter,
  fillColor: PaintBucket, borderColor: SquareDashed,
  arrowUp: ArrowUp, arrowDown: ArrowDown,
  arrowLeft: ArrowLeft, arrowRight: ArrowRight,
  // A line with a node at each end — the connector itself.
  connector: GitCommitHorizontal,
  playbook: ClipboardList, mcp: Server, general: Shapes, tenants: Network,
  statusPage: Activity, mergeField: Braces,
  add: Plus, tab: PanelTop, history: HistoryGlyph, palette: Palette,
};

/**
 * Nav icons we deliberately render from Lucide regardless of any per-family SVG
 * body still sitting in ICON_DATA — keeps them visually distinct + identical
 * across every theme. `tenants` used to reuse a building glyph that read exactly
 * like `companies`; force the Lucide `Network` glyph instead.
 */
const FORCE_LUCIDE_ICONS = new Set<IconName>(['tenants']);

/** Which icon family each theme palette uses. */
const PALETTE_FAMILY: Record<ThemePalette, IconFamily> = {
  'signal-cyan': 'lucide',
  'slate-command': 'lucide',
  'verdant-ops': 'ph',
  'indigo-grid': 'tabler',
  'rose-prism': 'heroicons',
  'custom': 'lucide', // custom themes use the default icon family
};

type IconProps = { size?: number | string; strokeWidth?: number; className?: string; style?: CSSProperties };

export function Icon({ name, size = 18, strokeWidth, className, style }: { name: IconName } & IconProps) {
  const palette = useThemeStore((s) => s.palette);
  const fam = PALETTE_FAMILY[palette] ?? 'lucide';
  const set = ICON_DATA[fam];
  // Forced-Lucide names skip the per-family SVG body so their Lucide component
  // (below) always wins — see FORCE_LUCIDE_ICONS.
  const body = FORCE_LUCIDE_ICONS.has(name) ? undefined : (set.icons[name] ?? ICON_DATA.lucide.icons[name]);
  // The global `img, svg { display: block }` reset would push an icon onto its
  // own line beside text in non-flex buttons/rows. Force inline-block so icons
  // flow next to their label (and stay a sized flex item in flex/grid rows).
  // Caller-supplied `style` wins via spread order.
  const iconStyle: CSSProperties = { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style };
  // Names with no SVG body in any family render via their Lucide component.
  if (!body) {
    const L = LUCIDE_COMPONENTS[name];
    if (L) {
      const px = typeof size === 'number' ? size : parseInt(size, 10) || 18;
      return <L size={px} strokeWidth={strokeWidth ?? 2} className={className} style={iconStyle} aria-hidden />;
    }
  }
  const vb = set.icons[name] ? set.vb : ICON_DATA.lucide.vb;
  return (
    <svg
      className={className}
      style={iconStyle}
      width={size}
      height={size}
      viewBox={vb}
      aria-hidden="true"
      focusable="false"
      dangerouslySetInnerHTML={{ __html: body ?? '' }}
    />
  );
}

/** Maps legacy emoji used in chrome/labels to a registry icon, so existing
 *  `icon="🔍"`-style props (and the conversion sweep) resolve to modern icons. */
export const EMOJI_TO_ICON: Record<string, IconName> = {
  // chrome / filters (original set)
  '🔍': 'search', '🔎': 'search', '⇅': 'sort', '🚦': 'status', '◐': 'status',
  '👁': 'view', '✓': 'check', '✅': 'check', '🏷': 'tag', '🏢': 'department',
  '📑': 'type', '🚂': 'releases', '🗂': 'folder', '📁': 'folder',
  // actions / objects
  '✨': 'ai', '🔷': 'workitem', '💬': 'comment', '🗑': 'delete', '📦': 'package',
  '🎫': 'tickets', '🎟': 'tickets', '🤖': 'analyze', '🔄': 'refresh', '🔃': 'refresh',
  '📘': 'userstory', '📚': 'sources', '🚀': 'rocket', '⚡': 'zap', '⚙': 'settings',
  '🔧': 'tools', '🛠': 'tools', '🏆': 'feature', '🏅': 'feature', '📄': 'note', '📃': 'note',
  '📓': 'note', '📝': 'edit', '🔗': 'link', '🧭': 'compass', '🔒': 'lock', '🔓': 'unlock',
  '🐞': 'bug', '🐛': 'bug', '👤': 'user', '🧑': 'user', '👥': 'users', '📋': 'copy',
  '📊': 'chart', '📈': 'chart', '✉': 'mail', '🧬': 'variant', '🕒': 'clock', '💼': 'briefcase',
  '📌': 'pin', '🖼': 'image', '🪙': 'credits', '📭': 'inbox', '📤': 'send', '🧹': 'clean',
  '🔀': 'merge', '🧪': 'test', '🏗': 'build', '🧩': 'puzzle', '🎓': 'learning', '📎': 'attach',
  '🤝': 'handshake', '🎉': 'celebrate', '🆕': 'new', '🙈': 'hide', '✂': 'cut', '🛰': 'satellite',
  '🤔': 'thinking', '🧠': 'thinking', '💭': 'thinking', '❓': 'help', '⚠': 'warning',
  '⏳': 'pending', '♻': 'recycle', '👀': 'view',
};

/**
 * A saved filter's own icon — one of the 96 catalog names (FilterIconName from
 * @pcp/shared, rendered through the static map in lib/filterIconMap.ts). It is
 * Lucide in every theme (like LUCIDE_COMPONENTS) and inherits currentColor, so
 * a filter colour is applied by setting `color` on it or its parent to
 * `var(--filter-color-<key>)`. Use it ONLY for user-chosen saved-filter icons;
 * bar chrome and quick-filter field icons stay on <Icon>. (f-02064)
 */
export function CatalogIcon({ name, size = 16, strokeWidth, className, style, title }: { name: FilterIconName; title?: string } & IconProps) {
  const L = FILTER_ICON_COMPONENTS[name] ?? FILTER_ICON_COMPONENTS.funnel;
  const px = typeof size === 'number' ? size : parseInt(size, 10) || 16;
  const iconStyle: CSSProperties = { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, ...style };
  return title
    ? <L size={px} strokeWidth={strokeWidth ?? 2} className={className} style={iconStyle} role="img" aria-label={title} />
    : <L size={px} strokeWidth={strokeWidth ?? 2} className={className} style={iconStyle} aria-hidden />;
}
